import { Fragment, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { useForbiddenGuard } from '../api/guard'
import { useAdminConnectors } from '../api/categories'
import { errorCode, titledErrorMessage } from '../api/problems'
import { ExposureEditor } from '../components/ExposureEditor'
import { ToolSelectionEditor } from '../components/ToolSelectionEditor'
import './screens.css'

function problemDetail(err: unknown): string {
  const d = (err as { detail?: unknown } | null)?.detail
  if (d && typeof d === 'object' && !Array.isArray(d)) {
    // A structured, non-problem detail (e.g. ingest validation) — show it raw + the code.
    const code = errorCode(err)
    return `${JSON.stringify(d)}${code ? ` [${code}]` : ''}`
  }
  return titledErrorMessage(err)
}

type Panel = 'exposure' | 'tools'

export function ConnectorsView() {
  const qc = useQueryClient()
  const [specText, setSpecText] = useState('')
  const [promoteRef, setPromoteRef] = useState('')
  const [promoteText, setPromoteText] = useState('')
  // One expanded panel per connector row: exposure/categories or tool selection.
  const [manage, setManage] = useState<{ id: string; panel: Panel } | null>(null)
  const togglePanel = (id: string, panel: Panel) =>
    setManage((m) => (m?.id === id && m.panel === panel ? null : { id, panel }))

  const query = useAdminConnectors()
  useForbiddenGuard(query.error)

  const ingest = useMutation({
    mutationFn: (raw: string) =>
      apiFetch('/admin/connectors/openapi', { method: 'POST', body: raw }),
    onSuccess: () => {
      setSpecText('')
      qc.invalidateQueries({ queryKey: ['admin-connectors'] })
    },
  })
  useForbiddenGuard(ingest.error)

  const promote = useMutation({
    mutationFn: (vars: { ref: string; raw: string }) =>
      apiFetch(`/admin/connectors/${encodeURIComponent(vars.ref)}/tools`, {
        method: 'POST',
        body: vars.raw,
      }),
    onSuccess: () => {
      setPromoteText('')
      qc.invalidateQueries({ queryKey: ['admin-connectors'] })
    },
  })
  useForbiddenGuard(promote.error)

  const connectors = query.data?.connectors ?? []
  const isOpen = (id: string, panel: Panel) => manage?.id === id && manage.panel === panel

  return (
    <section className="scr" aria-labelledby="connectors-heading">
      <div className="screen-head">
        <h2 id="connectors-heading">Connectors</h2>
      </div>

      {query.isError && (query.error as { status?: number }).status !== 403 && (
        <p className="screen-error" role="alert">
          Failed to load connectors.
        </p>
      )}

      <p className="scr-note">
        A <strong>dark</strong> connector is visible only to platform admins; going{' '}
        <strong>live</strong> needs 1–3 tool categories. Upstream MCP connectors also need a
        recorded vendor ToS review first (see MCP Upstreams). <strong>Tools</strong> picks which
        tools of an OpenAPI-ingested connector are exposed (built-ins are not curatable).
      </p>

      <table className="scr-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Version</th>
            <th>Tools</th>
            <th>Active</th>
            <th>Exposure</th>
            <th>Categories</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {connectors.map((c) => {
            const label = c.display_name || c.name
            return (
              <Fragment key={c.id}>
                <tr className={c.is_active ? undefined : 'scr-row--muted'}>
                  <td>
                    {label}
                    <div className="scr-muted scr-mono">{c.name}</div>
                  </td>
                  <td>{c.server_type ?? '—'}</td>
                  <td>{c.version ?? '—'}</td>
                  <td>
                    {c.active_tool_count}/{c.tool_count}
                  </td>
                  <td>{c.is_active ? 'Yes' : 'No'}</td>
                  <td>
                    <span
                      className={`scr-badge scr-badge--${c.exposure === 'live' ? 'active' : 'pending'}`}
                    >
                      {c.exposure ?? '—'}
                    </span>
                  </td>
                  <td>
                    <span className="scr-chips">
                      {(c.categories ?? []).length === 0 && <span className="scr-muted">—</span>}
                      {(c.categories ?? []).map((cat) => (
                        <span key={cat} className="scr-chip">
                          {cat}
                        </span>
                      ))}
                    </span>
                  </td>
                  <td>
                    <span className="scr-actions">
                      <button
                        type="button"
                        className="screen-btn"
                        aria-expanded={isOpen(c.id, 'exposure')}
                        aria-label={`Exposure and categories for ${label}`}
                        onClick={() => togglePanel(c.id, 'exposure')}
                      >
                        {isOpen(c.id, 'exposure') ? 'Close' : 'Exposure / categories'}
                      </button>
                      <button
                        type="button"
                        className="screen-btn"
                        aria-expanded={isOpen(c.id, 'tools')}
                        aria-label={`Tool selection for ${label}`}
                        onClick={() => togglePanel(c.id, 'tools')}
                      >
                        {isOpen(c.id, 'tools') ? 'Close' : 'Tools'}
                      </button>
                    </span>
                  </td>
                </tr>
                {isOpen(c.id, 'tools') && (
                  <tr>
                    <td colSpan={8}>
                      <ToolSelectionEditor connectorName={c.name} label={label} />
                    </td>
                  </tr>
                )}
                {isOpen(c.id, 'exposure') && (
                  <tr>
                    <td colSpan={8}>
                      <ExposureEditor
                        connectorName={c.name}
                        label={label}
                        exposure={c.exposure}
                        categories={c.categories}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
          {!query.isLoading && connectors.length === 0 && (
            <tr>
              <td colSpan={8} className="ws-empty">
                No connectors.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="scr-panel">
        <h3>Ingest OpenAPI connector</h3>
        <p className="scr-note">
          Paste the full request body JSON: <code>{'{ "spec": {…}, "auth": {…} }'}</code> (inline
          spec object, not a URL).
        </p>
        <textarea
          className="scr-textarea"
          aria-label="OpenAPI ingest request body"
          rows={6}
          value={specText}
          onChange={(e) => setSpecText(e.target.value)}
          placeholder={'{ "spec": { ... }, "auth": { "type": "apiKey", "secret_key_name": "..." } }'}
        />
        <div className="scr-panel-actions">
          <button
            type="button"
            className="screen-btn"
            disabled={!specText.trim() || ingest.isPending}
            onClick={() => ingest.mutate(specText)}
          >
            {ingest.isPending ? 'Ingesting…' : 'Ingest'}
          </button>
          {ingest.isSuccess && (
            <span className="scr-ok">
              Registered {(ingest.data as { connector_name?: string })?.connector_name} —{' '}
              {(ingest.data as { tool_count?: number })?.tool_count} tools
            </span>
          )}
          {ingest.isError && (ingest.error as { status?: number }).status !== 403 && (
            <span className="scr-err" role="alert">
              {problemDetail(ingest.error)}
            </span>
          )}
        </div>
      </div>

      <div className="scr-panel">
        <h3>Promote a GET/HEAD tool</h3>
        <p className="scr-note">
          Connector name + the promote request body JSON:{' '}
          <code>{'{ "method": "GET", "path_template": "...", "param_map": [...], "name": "...", "description": "..." }'}</code>
        </p>
        <input
          type="text"
          className="scr-input"
          aria-label="Connector name"
          placeholder="connector name (OpenAPI-ingested only)"
          value={promoteRef}
          onChange={(e) => setPromoteRef(e.target.value)}
        />
        <textarea
          className="scr-textarea"
          aria-label="Promote tool request body"
          rows={5}
          value={promoteText}
          onChange={(e) => setPromoteText(e.target.value)}
        />
        <div className="scr-panel-actions">
          <button
            type="button"
            className="screen-btn"
            disabled={!promoteRef.trim() || !promoteText.trim() || promote.isPending}
            onClick={() => promote.mutate({ ref: promoteRef.trim(), raw: promoteText })}
          >
            {promote.isPending ? 'Promoting…' : 'Promote'}
          </button>
          {promote.isSuccess && (
            <span className="scr-ok">
              Promoted {(promote.data as { tool_name?: string })?.tool_name}
            </span>
          )}
          {promote.isError && (promote.error as { status?: number }).status !== 403 && (
            <span className="scr-err" role="alert">
              {problemDetail(promote.error)}
            </span>
          )}
        </div>
      </div>
    </section>
  )
}
